import React, { useState, useMemo, type SVGProps, useEffect } from 'react'
import { Box, Chip, Menu, MenuItem, ListItemText } from '@mui/material'

import { type CardActivityWithId } from '@/models/card-activity/card-activity.model'

interface LeniencyOption {
  label: string
  value: string
}

const leniencyOptions: LeniencyOption[] = [
  { label: 'Build Confidence - most lenient', value: 'confidence' },
  { label: 'Very Lenient', value: 'easy' },
  { label: 'Normal', value: 'normal' },
  { label: 'No leniency - most strict', value: 'hard' },
]

interface LeniencySelectorProps {
  card: CardActivityWithId
  updateCard: (id: string, field: keyof CardActivityWithId, value: Partial<CardActivityWithId>) => void
}

export function LeniencySelector({ card, updateCard }: LeniencySelectorProps): JSX.Element {
  const [selectedMethod, setSelectedMethod] = useState<string>(card?.difficulty ?? 'normal')
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null)

  const handleOpen = (event: React.MouseEvent<HTMLDivElement>) => {
    event.preventDefault()
    event.stopPropagation()
    setAnchorEl(event.currentTarget)
  }

  const handleClose = () => {
    setAnchorEl(null)
  }

  const handleSelect = (value: string) => {
    updateCard(card.id, 'difficulty', value)
    setSelectedMethod(value)
    handleClose()
  }

  useEffect(() => {
    setSelectedMethod(card?.difficulty)
  }, [card?.difficulty])

  const currentChip = useMemo(() => leniencyOptions.find(opt => opt.value === selectedMethod), [selectedMethod])

  const chipStyles = {
    cursor: 'pointer',
    fontWeight: 500,
    padding: '8px 16px 8px 8px',
    gap: '8px',
    borderRadius: '16px',
    background: '#F2F4F7',
    height: 42,
    '& .MuiChip-label': { fontSize: 14, p: 0 },
    '& .MuiChip-icon': { m: 0 },
  }

  const menuStyles = {
    '& .MuiPaper-root': {
      borderRadius: '16px',
      border: '1px solid #EAECF0',
      background: '#FFF',
      boxShadow: '0px 12px 16px -4px rgba(16, 24, 40, 0.08), 0px 4px 6px -2px rgba(16, 24, 40, 0.03)',
    },
  }

  return (
    <Box alignItems="center" display="flex" gap={2}>
      <Chip
        icon={<LeniencySelectorIcon />}
        label={currentChip?.label.split('-')[0]}
        sx={chipStyles}
        onClick={handleOpen}
      />

      <Menu
        anchorEl={anchorEl}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
        open={Boolean(anchorEl)}
        sx={menuStyles}
        transformOrigin={{ vertical: 'top', horizontal: 'left' }}
        onClose={handleClose}
      >
        {leniencyOptions.map(option => (
          <MenuItem
            key={option.value}
            selected={option.value === selectedMethod}
            onClick={e => {
              e.preventDefault()
              e.stopPropagation()
              handleSelect(option.value)
            }}
          >
            <ListItemText primary={option.label} />
          </MenuItem>
        ))}
      </Menu>
    </Box>
  )
}

export default LeniencySelector

function LeniencySelectorIcon(props: SVGProps<SVGSVGElement>) {
  return (
    <svg fill="none" height={30} viewBox="0 0 31 30" width={31} xmlns="http://www.w3.org/2000/svg" {...props}>
      <path
        d="M10.9997 1.13333C5.73456 1.13333 1.46634 5.40155 1.46634 10.6667V19.3333C1.46634 24.5984 5.73456 28.8667 10.9997 28.8667H19.6663C24.9315 28.8667 29.1997 24.5984 29.1997 19.3333V10.6667C29.1997 5.40155 24.9315 1.13333 19.6663 1.13333H10.9997Z"
        fill="#86CB3C"
      />
      <path
        d="M10.9997 1.13333C5.73456 1.13333 1.46634 5.40155 1.46634 10.6667V19.3333C1.46634 24.5984 5.73456 28.8667 10.9997 28.8667H19.6663C24.9315 28.8667 29.1997 24.5984 29.1997 19.3333V10.6667C29.1997 5.40155 24.9315 1.13333 19.6663 1.13333H10.9997Z"
        stroke="#F5FBEE"
        strokeWidth={1.73333}
      />
      <g clipPath="url(#clip0_3442_7659)">
        <path
          d="M11.7219 13.7953L13.7888 15.8622C13.908 15.9814 13.9676 16.041 14.0363 16.0633C14.0967 16.083 14.1618 16.083 14.2223 16.0633C14.291 16.041 14.3506 15.9814 14.4697 15.8622L16.1962 14.1357C16.3154 14.0166 16.375 13.957 16.4437 13.9347C16.5041 13.915 16.5692 13.915 16.6297 13.9347C16.6984 13.957 16.758 14.0166 16.8771 14.1357L18.9441 16.2027M21.3515 14.999C21.3515 18.3229 18.6569 21.0175 15.333 21.0175C12.009 21.0175 9.31445 18.3229 9.31445 14.999C9.31445 11.6751 12.009 8.98047 15.333 8.98047C18.6569 8.98047 21.3515 11.6751 21.3515 14.999Z"
          stroke="white"
          strokeLinecap="round"
          strokeLinejoin="round"
        />
      </g>
      <defs>
        <clipPath id="clip0_3442_7659">
          <rect fill="white" height={14.4444} transform="translate(8.11035 7.77734)" width={14.4444} />
        </clipPath>
      </defs>
    </svg>
  )
}
